import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom';
import SearchableDropdown from '../components/SearchableDropdown'
import ChestionarErrorBoundary from '../components/ChestionarErrorBoundary'
import ChestionarLoadingState from '../components/ChestionarLoadingState'
import UiCopyBlock from '../components/UiCopyBlock'
import SuccessPage from './SuccessPage'
import LinkUsedPage from './LinkUsedPage'
import LinkExpiredPage from './LinkExpiredPage'
import { Question, FeedbackContext, AnswerPayload, UiCopy } from '../types/entities'
import { checkSubmissionRateLimit, recordSubmission } from '../utils/rateLimiter'
import { 
  isValidTokenFormat
} from '../utils/tokenUtils'
import { 
  validateToken, 
  getQuestionnaire, 
  submitAnswers,
  APIError
} from '../utils/api'

// Type for doctor in dropdown format
interface DoctorForDropdown {
  id: string;
  label: string;
  specialization: string;
}

const ALTCEVA_PREFIX = 'Altele - ';
const ALTCEVA_LABEL = 'Altele (vă rugăm specificați)';

// Stateful sub-component for question type = 'variants'
const VariantsQuestion: React.FC<{
  question: Question
  value: string           // current stored value (e.g. "Da" or "Altele - free text")
  onChange: (value: string) => void
  autoFocus?: boolean
  showAltele?: boolean    // when false, renders plain radio buttons without the Altele option
}> = ({ question, value, onChange, autoFocus = false, showAltele = true }) => {
  // Derive selected option and otherText from stored value
  const isAltcevaStored = value.startsWith(ALTCEVA_PREFIX);
  const [selected, setSelected] = useState<string>(isAltcevaStored ? ALTCEVA_LABEL : value);
  const [otherText, setOtherText] = useState<string>(isAltcevaStored ? value.slice(ALTCEVA_PREFIX.length) : '');

  const options: string[] = Array.isArray(question.options) ? question.options : [];
  const allOptions = showAltele ? [...options, ALTCEVA_LABEL] : options;

  const handleSelect = (opt: string) => {
    setSelected(opt);
    if (opt === ALTCEVA_LABEL) {
      // Keep whatever is already in otherText; notify parent only when text is present
      onChange(otherText.trim() ? ALTCEVA_PREFIX + otherText.trim() : '');
    } else {
      setOtherText('');
      onChange(opt);
    }
  };

  const handleOtherText = (text: string) => {
    setOtherText(text);
    onChange(text.trim() ? ALTCEVA_PREFIX + text.trim() : '');
  };

  return (
    <div className="space-y-3 mt-3">
      {allOptions.map((opt, index) => {
        const isSelected = selected === opt;
        return (
          <label
            key={opt}
            className="flex items-center space-x-4 cursor-pointer group py-3 px-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
              ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-slate-300 group-hover:border-blue-400'}`}>
              {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full" />}
            </div>
            <input
              type="radio"
              name={`question-${question.id}`}
              value={opt}
              checked={isSelected}
              onChange={() => handleSelect(opt)}
              className="hidden"
              autoFocus={autoFocus && index === 0}
            />
            <span className={`text-base font-medium flex-1 ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
              {opt}
            </span>
          </label>
        );
      })}

      {selected === ALTCEVA_LABEL && (
        <div className="mt-2 space-y-1">
          <label className="block text-sm font-medium text-slate-600">
            Vă rugăm specificați:
          </label>
          <textarea
            value={otherText}
            onChange={(e) => handleOtherText(e.target.value)}
            rows={3}
            placeholder="Descrieți în câteva cuvinte..."
            autoFocus
            className="w-full bg-white border border-slate-300 text-slate-700 py-3 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-base resize-none touch-manipulation"
          />
          {otherText.trim() === '' && (
            <p className="text-xs text-red-500">Câmp obligatoriu când este selectată această opțiune.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Question Renderer Component
const QuestionRenderer: React.FC<{
  question: Question
  value: string
  onChange: (value: string) => void
  doctors: DoctorForDropdown[]
  specializations: { label: string; value: string }[]
  autoFocus?: boolean
  tipServicii?: string | null
  isFirstQuestion?: boolean
}> = ({ question, value, onChange, doctors, specializations, autoFocus = false, tipServicii, isFirstQuestion = false }) => {

  // "Altele" option only on the first question of service type RAD
  const showAltele = tipServicii === 'RAD' && isFirstQuestion;

  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-4 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-base font-medium resize-none min-h-[120px] touch-manipulation"
            placeholder="Scrieți răspunsul aici..."
            rows={4}
            autoFocus={autoFocus}
          />
        )
      
      case 'yesno':
        return (
          <div className="space-y-3 mt-3">
            {['Da', 'Nu'].map((option) => (
              <label key={option} className="flex items-center space-x-4 cursor-pointer group py-3 px-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
                  ${value === option ? 'border-blue-600 bg-blue-50' : 'border-slate-300 group-hover:border-blue-400'}`}>
                  {value === option && <div className="w-3 h-3 bg-blue-600 rounded-full" />}
                </div>
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => onChange(e.target.value)}
                  className="hidden"
                  autoFocus={autoFocus && option === 'Da'}
                />
                <span className={`text-base font-medium flex-1 ${value === option ? 'text-slate-900' : 'text-slate-600'}`}>
                  {option}
                </span>
              </label>
            ))}
          </div>
        )
      
      case 'rating':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-center bg-slate-50 p-2 sm:p-2.5 rounded-xl border border-slate-100 mt-3 gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => onChange(rating.toString())}
                  className={`flex-1 min-h-[48px] sm:min-h-[52px] max-w-[64px] rounded-lg flex items-center justify-center transition-all font-semibold text-base sm:text-lg touch-manipulation active:scale-95 ${
                    value === rating.toString()
                      ? 'bg-blue-600 text-white shadow-md scale-105'
                      : 'text-slate-500 hover:bg-white hover:shadow-sm hover:text-blue-600'
                  }`}
                  autoFocus={autoFocus && rating === 1}
                >
                  {rating}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] sm:text-xs text-slate-400 mt-2 px-1 uppercase tracking-wider font-medium">
              <span>Nesatisfăcut</span>
              <span>Satisfăcut</span>
            </div>
          </div>
        )
      
      case 'nps':
        // Support custom max value from options, default to 10
        const maxNpsValue = question.options?.max || 10;
        const npsLength = maxNpsValue + 1; // +1 because we start from 0
        
        // FIXED: Replaced scroll view with a Flex Wrap Grid
        // This ensures all numbers (0-10) are visible at once on mobile (usually 2 rows)
        return (
          <div className="space-y-3">
            <div className="bg-slate-50 p-3 sm:p-4 rounded-xl border border-slate-100 mt-3">
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {Array.from({ length: npsLength }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onChange(i.toString())}
                    className={`w-10 h-10 sm:w-12 sm:h-12 flex-none rounded-lg flex items-center justify-center font-semibold text-sm sm:text-base transition-all touch-manipulation active:scale-95 ${
                      value === i.toString()
                        ? 'bg-blue-600 text-white shadow-md scale-110 ring-2 ring-blue-200'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-400 hover:text-blue-600 shadow-sm'
                    }`}
                    autoFocus={autoFocus && i === 0}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-between text-[10px] sm:text-xs text-slate-400 mt-2 px-1 uppercase tracking-wider font-medium">
              <span>Deloc probabil</span>
              <span>Foarte probabil</span>
            </div>
          </div>
        )

      case 'variants':
        return (
          <VariantsQuestion
            question={question}
            value={value}
            onChange={onChange}
            autoFocus={autoFocus}
            showAltele={showAltele}
          />
        )

      case 'singlechoice':
        return (
          <div className="space-y-3 mt-3">
            {question.options?.map((option: string, index: number) => {
              const cleanOption = option
                .replace(/☐/g, '')
                .replace(/□/g, '')
                .replace(/☑/g, '')
                .replace(/■/g, '')
                .replace(/\u2610/g, '')
                .replace(/\u2611/g, '')
                .replace(/\u25a1/g, '')
                .replace(/\u25a0/g, '')
                .trim();
              
              // For single choice, check against cleaned value
              const isSelected = value === cleanOption;
              
              return (
                <label key={option} className="flex items-center space-x-4 cursor-pointer group py-3 px-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
                    ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-slate-300 group-hover:border-blue-400'}`}>
                    {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full" />}
                  </div>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={cleanOption}
                    checked={isSelected}
                    onChange={(e) => onChange(e.target.value)}
                    className="hidden"
                    autoFocus={autoFocus && index === 0}
                  />
                  <span className={`text-base font-medium flex-1 ${isSelected ? 'text-slate-900' : 'text-slate-600'}`}>
                    {cleanOption}
                  </span>
                </label>
              );
            })}
          </div>
        )

      case 'multichoice':
      case 'multiple_choice':
        // NEW: Check allow_multiple_selection to determine if multiple selections are allowed
        const allowMultiple = question.allow_multiple_selection ?? true; // Default to true for backward compatibility
        
        if (!allowMultiple) {
          // Render as single-choice with Altele support when allow_multiple_selection is false
          return (
            <VariantsQuestion
              question={question}
              value={value}
              onChange={onChange}
              autoFocus={autoFocus}
              showAltele={showAltele}
            />
          );
        }
        
        // Render as multi-choice (checkboxes) when allow_multiple_selection is true
        return (
          <div className="space-y-3 mt-3">
            {question.options?.map((option: string, index: number) => {
              const cleanOption = option
                .replace(/☐/g, '')
                .replace(/□/g, '')
                .replace(/☑/g, '')
                .replace(/■/g, '')
                .replace(/\u2610/g, '')
                .replace(/\u2611/g, '')
                .replace(/\u25a1/g, '')
                .replace(/\u25a0/g, '')
                .trim();
              
              // For multi-choice, check if cleaned option is in the comma-separated value
              const currentValues = value ? value.split(',').map(v => v.trim()) : [];
              const isChecked = currentValues.includes(cleanOption);
              
              return (
                <label key={option} className="flex items-center space-x-4 cursor-pointer group py-3 px-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation">
                  <input
                    type="checkbox"
                    value={cleanOption}
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Add the cleaned option to the list
                        const newValues = [...currentValues, cleanOption];
                        onChange(newValues.join(','));
                      } else {
                        // Remove the cleaned option from the list
                        const newValues = currentValues.filter(v => v !== cleanOption);
                        onChange(newValues.join(','));
                      }
                    }}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded flex-shrink-0 touch-manipulation"
                    autoFocus={autoFocus && index === 0}
                  />
                  <span className="text-base font-medium text-slate-700 flex-1">{cleanOption}</span>
                </label>
              );
            })}
          </div>
        )

      case 'specialization_dropdown':
        if (specializations.length === 0) {
          return (
            <div className="relative mt-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                Nu sunt specializări disponibile. Contactați administratorul.
              </p>
            </div>
          );
        }
        
        return (
          <div className="relative mt-2">
            <SearchableDropdown
              options={specializations.map(s => ({ 
                value: s.value, 
                label: s.label 
              }))}
              value={value}
              onChange={onChange}
              placeholder="Alegeți specializarea..."
              loading={false}
              disabled={false}
            />
          </div>
        )

      case 'doctor_dropdown':
        if (doctors.length === 0) {
          return (
            <div className="relative mt-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                Nu sunt doctori disponibili. Verificați dacă există doctori configurați pentru acest serviciu.
              </p>
            </div>
          );
        }
        
        return (
          <div className="relative mt-2">
            <SearchableDropdown
              options={doctors.map(doctor => ({ 
                value: String(doctor.id), 
                label: String(doctor.label) 
              }))}
              value={value}
              onChange={onChange}
              placeholder="Alegeți doctorul..."
              loading={false}
              disabled={false}
            />
            {doctors.length > 0 && (
              <p className="mt-2 text-xs sm:text-sm text-slate-500">
                {doctors.length} {doctors.length === 1 ? 'doctor disponibil' : 'doctori disponibili'}
              </p>
            )}
          </div>
        )
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-4 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-base font-medium placeholder-slate-400 touch-manipulation"
            placeholder="Scrieți răspunsul aici..."
            autoFocus={autoFocus}
          />
        )
    }
  }

  // Clean question label by removing checkbox characters
  const cleanLabel = (label: string): string => {
    return label
      .replace(/☐/g, '') // Remove empty checkbox
      .replace(/□/g, '') // Remove empty box
      .replace(/☑/g, '') // Remove checked checkbox
      .replace(/■/g, '') // Remove filled box
      .replace(/\u2610/g, '') // Unicode empty checkbox
      .replace(/\u2611/g, '') // Unicode checked checkbox
      .replace(/\u25a1/g, '') // Unicode white square
      .replace(/\u25a0/g, '') // Unicode black square
      .trim();
  };

  return (
    <div className="mb-5 sm:mb-7 min-w-0">
      {/* Question text is justified (Word's "stânga-dreapta"). hyphens-auto
          matters here: without it, justifying a long Romanian sentence in a
          narrow phone column stretches the word gaps into visible rivers.
          The page sets lang="ro", so breaks follow Romanian rules. */}
      <label className="block text-[15px] sm:text-base font-semibold text-slate-700 mb-3 break-words hyphens-auto text-justify leading-snug">
        {cleanLabel(question.label)}
        {question.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {/* Answers stay left-aligned regardless of the question's alignment —
          pinned here rather than on each option so every input type (radio,
          checkbox, rating, textarea, dropdown) is covered from one place. */}
      <div className="text-left">
        {renderInput()}
      </div>
    </div>
  )
}

/**
 * Returns `override` when it is a non-empty string after trimming,
 * otherwise returns `fallback`. Keeps the UI unchanged when ui_copy is absent.
 */
function getUiText(override: string | undefined, fallback: string): string {
  const t = (override ?? '').trim();
  return t.length > 0 ? t : fallback;
}

// Main Component
const ChestionarPublicPageContent: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Extract token from URL search parameters safely
  const token = searchParams.get("token");
  
  /**
   * CRITICAL: Guard to prevent duplicate loads in React StrictMode (dev)
   * This ref ensures we only load data ONCE per token value, even with double-invoke
   */
  const didLoadRef = useRef<boolean>(false);
  const currentTokenRef = useRef<string | null>(null);
  
  // State
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [feedbackContext, setFeedbackContext] = useState<FeedbackContext | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [doctors, setDoctors] = useState<DoctorForDropdown[]>([])
  const [filteredDoctors, setFilteredDoctors] = useState<DoctorForDropdown[]>([])
  const [specializations, setSpecializations] = useState<{ label: string; value: string }[]>([])
  const [uiCopy, setUiCopy] = useState<Partial<UiCopy>>({})
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [isPageReady, setIsPageReady] = useState(false) // NEW: tracks if page is fully ready to display
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null) // Track error code separately
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [missingRequiredFields, setMissingRequiredFields] = useState<Question[]>([])
  // Generic validation message for cases that aren't "missing required fields"
  // (empty submit, text-only submit). When set, the modal renders this
  // title/body instead of the required-fields list.
  const [validationMessage, setValidationMessage] = useState<{ title: string; body: string } | null>(null)
  const [isHydrated, setIsHydrated] = useState(false)
  const [unsubscribe, setUnsubscribe] = useState(false)

  // Mobile-safe hydration check
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Error handling setup
  useEffect(() => {
    const handleError = (_event: ErrorEvent) => {
      setError('A apărut o eroare neașteptată. Vă rugăm să reîncărcați pagina.')
    }

    const handleUnhandledRejection = (_event: PromiseRejectionEvent) => {
      setError('A apărut o eroare de rețea. Vă rugăm să verificați conexiunea și să reîncărcați pagina.')
    }

    const handleResourceError = (event: Event) => {
      
      // Retry mechanism for asset loading
      const target = event.target as HTMLElement
      if (target && 'src' in target) {
        const originalSrc = (target as any).src
        setTimeout(() => {
          (target as any).src = originalSrc + '?retry=' + Date.now()
        }, 1000)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('error', handleResourceError, true)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleResourceError, true)
    }
  }, [])

  /**
   * UNIFIED DATA LOADING FUNCTION
   * - Validates token (ONCE)
   * - Fetches complete questionnaire snapshot (ONCE)
   * - Sets all state from single response
   */
  const loadAllData = async (rawToken: string) => {
    try {
      setLoading(true);
      setError(null);

      // Decode percent-encoded tokens that may arrive via SMS/email links
      let token: string;
      try {
        token = decodeURIComponent(rawToken).trim();
      } catch {
        token = rawToken.trim();
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug(`[survey] token length=${token.length}`);
      }

      // Step 1: Validate token format first (accepts 16-char base62 + legacy base64url)
      if (!isValidTokenFormat(token)) {
        setError('Link invalid sau incomplet. Vă rugăm să verificați link-ul primit.');
        setLoading(false);
        setIsPageReady(true);
        return;
      }

      // Step 2: Validate token and get session token (CALL 1)
      const validationResponse = await validateToken(token);

      // Store session token
      setSessionToken(validationResponse.session_token);

      // Step 3: Fetch complete questionnaire snapshot using session token (CALL 2)
      const snapshot = await getQuestionnaire(validationResponse.session_token);

      // Step 4: Set all state from the unified snapshot
      setFeedbackContext({
        hospital_id: snapshot.hospital.id,
        hospital_name: snapshot.hospital.name,
        tip_servicii: snapshot.link.tip_servicii || null,
        logo_url: snapshot.hospital.logo_url || null,
        brand_primary: snapshot.hospital.brand_primary || null,
        caz_id: snapshot.link.caz_id || '',
        link_id: snapshot.link.link_id,
        section_name: snapshot.link.section_name || null,
      });

      // Validate and set questions
      if (!snapshot.questions || snapshot.questions.length === 0) {
        setError("Nu există întrebări configurate pentru acest tip de serviciu.");
        setQuestions([]);
        setDoctors([]);
        setSpecializations([]);
        setLoading(false);
        setIsPageReady(true);
        return;
      }

      // Sort questions by position
      const sortedQuestions = snapshot.questions.sort(
        (a: Question, b: Question) => {
          const posA = a.position ?? a.question_position ?? a.order_index ?? 0;
          const posB = b.position ?? b.question_position ?? b.order_index ?? 0;
          return posA - posB;
        }
      );
      setQuestions(sortedQuestions);

      // Map doctors to dropdown format
      const apiDoctors = snapshot.doctors || [];
      const mappedDoctors: DoctorForDropdown[] = apiDoctors
        .filter((doctor: any) => doctor && doctor.id && (doctor.full_name || doctor.name))
        .map((doctor: any) => ({
          id: String(doctor.id),
          label: String(doctor.full_name || doctor.name),
          specialization: String(doctor.specialization || '')
        }));
      setDoctors(mappedDoctors);

      // Set specializations - backend returns array of strings, convert to {label, value}
      const apiSpecializations = snapshot.specializations || [];
      const specializations = apiSpecializations
        .filter((s) => s && typeof s === 'string')
        .map((s) => ({
          label: s,
          value: s
        }));
      
      setSpecializations(specializations);

      // Store ui_copy safely (may be absent on older snapshots)
      setUiCopy(snapshot.ui_copy ?? {});

      // All data loaded successfully
      setLoading(false);
      setIsPageReady(true);

    } catch (error) {
      if (error instanceof APIError) {
        // Handle specific API errors
        if (error.statusCode === 409 || error.errorCode === 'LINK_ALREADY_USED') {
          setError('Acest link de feedback a fost deja utilizat.');
          setErrorCode('LINK_ALREADY_USED');
          
          // Preserve hospital context from error metadata
          if (error.metadata?.hospital_id) {
            setFeedbackContext({
              hospital_id: error.metadata.hospital_id,
              hospital_name: error.metadata.hospital_name || '',
              tip_servicii: error.metadata.tip_servicii || null,
              logo_url: error.metadata.logo_url || null,
              caz_id: '',
              link_id: error.metadata.link_id || '',
            });
          }
        } else if (error.errorCode === 'LINK_EXPIRED') {
          setError('Acest link de feedback a expirat.');
          setErrorCode('LINK_EXPIRED');
          
          if (error.metadata?.hospital_id) {
            setFeedbackContext({
              hospital_id: error.metadata.hospital_id,
              hospital_name: error.metadata.hospital_name || '',
              tip_servicii: error.metadata.tip_servicii || null,
              logo_url: error.metadata.logo_url || null,
              caz_id: '',
              link_id: error.metadata.link_id || '',
            });
          }
        } else if (error.errorCode === 'LINK_NOT_FOUND') {
          setError('Link invalid sau expirat.');
          setErrorCode('LINK_NOT_FOUND');
        } else {
          setError(error.message);
          setErrorCode(error.errorCode || null);
        }
      } else {
        setError(error instanceof Error ? error.message : 'A apărut o eroare neașteptată.');
        setErrorCode(null);
      }
      
      setLoading(false);
      setIsPageReady(true);
    }
  };

  /**
   * CRITICAL: React StrictMode guard
   * - Prevents double-fetch in dev mode
   * - Resets when token changes
   * - Ensures EXACTLY 1 validate-token + 1 questionnaire call
   */
  useEffect(() => {
    if (!token) {
      setLoading(false);
      setIsPageReady(true);
      return;
    }

    // Token changed - reset the guard
    if (currentTokenRef.current !== token) {
      didLoadRef.current = false;
      currentTokenRef.current = token;
    }

    // Prevent duplicate loads (React StrictMode double-invoke)
    if (didLoadRef.current) {
      console.log('[SURVEY] Skipping duplicate load (StrictMode guard)');
      return;
    }

    // Mark as loaded BEFORE async call to prevent race conditions
    didLoadRef.current = true;

    // Load all data (validate-token + questionnaire)
    loadAllData(token);
  }, [token]);

  // Cleanup effect for HMR (dev mode)
  useEffect(() => {
    return () => {
      if (import.meta.hot) {
        didLoadRef.current = false;
        currentTokenRef.current = null;
      }
    };
  }, []);

  // Handle answer changes
  const handleAnswerChange = useCallback((questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }, [])

  useEffect(() => {
    // With the simplified doctor structure {id, label}, show all doctors
    setFilteredDoctors(doctors);
  }, [doctors]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!feedbackContext?.hospital_id || !token || !sessionToken) {
      setError('Sesiunea a expirat. Vă rugăm să accesați din nou link-ul.')
      return
    }

    // Check frontend rate limiting first
    const rateLimitCheck = checkSubmissionRateLimit(feedbackContext.hospital_id, token)

    if (!rateLimitCheck.canSubmit) {
      if (rateLimitCheck.reason === 'TOKEN_ALREADY_USED') {
        setError('Acest link de feedback a fost deja utilizat. Fiecare link poate fi folosit o singură dată.')
        return
      } else if (rateLimitCheck.reason === 'HOSPITAL_RATE_LIMIT_EXCEEDED') {
        setError(`Prea multe chestionare trimise recent de la acest spital. Încercați din nou mai târziu.`)
        return
      } else if (rateLimitCheck.reason === 'IP_RATE_LIMIT_EXCEEDED') {
        setError(`Prea multe chestionare trimise de pe acest dispozitiv. Încercați din nou mai târziu.`)
        return
      } else {
        setError('Nu se poate trimite chestionarul în acest moment. Încercați din nou mai târziu.')
        return
      }
    }

    // Validate required fields
    const missingFields = questions.filter(question =>
      question.is_required && (!answers[question.id] || answers[question.id].trim() === '')
    )

    if (missingFields.length > 0) {
      setValidationMessage(null)
      setMissingRequiredFields(missingFields)
      setShowValidationModal(true)
      return
    }

    // Block empty + text-only submissions. The stats report assumes every
    // submission contributes to at least one question with answer variants;
    // letting through empty / text-only submissions skews the
    // "respondenți / întrebare" alignment vs total respondents.
    const nonEmptyEntries = Object.entries(answers).filter(([, v]) => v && v.trim() !== '')
    if (nonEmptyEntries.length === 0) {
      setMissingRequiredFields([])
      setValidationMessage({
        title: 'Chestionar incomplet',
        body:  'Vă rugăm să răspundeți la cel puțin o întrebare înainte de a trimite chestionarul.',
      })
      setShowValidationModal(true)
      return
    }
    const hasNonTextAnswer = nonEmptyEntries.some(([qid]) => {
      const q = questions.find(qq => qq.id === qid)
      return q && q.type !== 'text'
    })
    if (!hasNonTextAnswer) {
      setMissingRequiredFields([])
      setValidationMessage({
        title: 'Chestionar incomplet',
        body:  'Vă rugăm să răspundeți la cel puțin o întrebare cu variante de răspuns (nu doar la întrebările libere) înainte de a trimite chestionarul.',
      })
      setShowValidationModal(true)
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Strict UUID validation & skip empty keys
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

      // Build minimal answer payload with only question_id and value
      const answerPayload: AnswerPayload[] = Object.entries(answers)
        .filter(([questionId, answer]) => {
          if (!questionId || typeof questionId !== "string" || questionId.trim() === "") return false
          
          if (!uuidRegex.test(questionId.trim())) return false
          
          const question = questions.find(q => q.id === questionId.trim());
          if (!question) return false;

          let finalAnswer = answer;
          if (question.type === 'doctor_dropdown') {
            const doctor = doctors.find(d => d.id === answer);
            if (doctor) finalAnswer = doctor.label;
          }
          
          if (!finalAnswer || typeof finalAnswer !== "string" || finalAnswer.trim() === "") return false
          return true
        })
        .map(([questionId, answer]) => {
          const question = questions.find(q => q.id === questionId.trim());
          let finalValue = answer;
          
          if (question && question.type === 'doctor_dropdown') {
            const doctor = doctors.find(d => d.id === answer);
            if (doctor) {
              finalValue = doctor.label;
            }
          }

          return {
            question_id: questionId.trim(),
            value: finalValue.trim(),
          };
        })

      if (answerPayload.length === 0) {
        setError('Nu există răspunsuri valide de trimis. Vă rugăm să completați cel puțin un câmp.')
        setSubmitting(false);
        return
      }

      // Call API to submit feedback with session token (Authorization header) + link token (body)
      const response = await submitAnswers(sessionToken, token, answerPayload, unsubscribe);

      if (response.success) {
        // Record submission in local storage for frontend rate limiting
        recordSubmission(feedbackContext.hospital_id, token);
        setSubmitted(true)
        setError('')
      } else {
        setError(response.message || "Trimiterea a eșuat. Vă rugăm să încercați din nou.");
      }

    } catch (error) {
      if (error instanceof APIError) {
        // Handle specific error codes with Romanian messages
        if (error.errorCode === 'SESSION_EXPIRED' || error.statusCode === 401) {
          setError('Sesiunea a expirat. Pagina va fi reîncărcată automat...');
          // Auto-reload after 2 seconds for session expiry
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else if (error.errorCode === 'VALIDATION_ERROR') {
          setError('Date invalide. Reîncearcă.');
        } else if (error.errorCode === 'LINK_ALREADY_USED') {
          setError('Chestionarul a fost deja completat.');
        } else if (error.errorCode === 'LINK_EXPIRED') {
          setError('Link expirat.');
        } else if (error.errorCode === 'LINK_NOT_FOUND') {
          setError('Link invalid.');
        } else if (error.errorCode === 'DATABASE_ERROR') {
          setError('Eroare de server. Vă rugăm să încercați din nou.');
        } else if (error.errorCode === 'RATE_LIMIT_EXCEEDED') {
          setError('Prea multe încercări. Vă rugăm să încercați din nou mai târziu.');
        } else {
          setError(error.message || 'A apărut o eroare. Vă rugăm să încercați din nou.');
        }
      } else {
        setError('A apărut o eroare la trimiterea răspunsurilor. Vă rugăm să încercați din nou.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // SSR/Hydration safeguards
  if (typeof window === 'undefined') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Se încarcă...</p>
        </div>
      </div>
    )
  }

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Se încarcă...</p>
        </div>
      </div>
    )
  }

// --- HARD GUARD: If link is expired or used, STOP THE ENTIRE COMPONENT ---
if (error) {
  const isLinkUsedError = errorCode === 'LINK_ALREADY_USED' ||
    error.includes("deja utilizat") ||
    error.includes("deja folosit") ||
    error.includes("already been used") ||
    error.includes("LINK_ALREADY_USED");

  const isLinkExpiredError = errorCode === 'LINK_EXPIRED' ||
    error.includes("expirat") ||
    error.includes("expired") ||
    error.includes("Link expired");

  if (isLinkUsedError) {
    return <LinkUsedPage hospitalId={feedbackContext?.hospital_id} logoUrl={feedbackContext?.logo_url} />;
  }

  if (isLinkExpiredError) {
    return <LinkExpiredPage hospitalId={feedbackContext?.hospital_id} logoUrl={feedbackContext?.logo_url} />;
  }
}

  // Asset loading error fallback with retry
  if (error && error.includes('asset')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">Eroare de încărcare</h3>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white px-4 py-3 sm:py-3.5 rounded-xl hover:bg-blue-700 font-semibold text-base touch-manipulation active:scale-[0.98] transition-all"
          >
            Reîncarcă pagina
          </button>
        </div>
      </div>
    )
  }

  // Missing token state
  if (!token && !loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 text-center">
          <div className="mb-4 sm:mb-6">
            <div className="mx-auto flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-yellow-50 border-2 border-yellow-200">
              <svg className="h-7 w-7 sm:h-8 sm:w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Link invalid
          </h3>
          <p className="text-sm sm:text-base text-gray-600">
            Vă rugăm să accesați chestionarul prin link-ul primit de la spital.
          </p>
        </div>
      </div>
    )
  }

  // Loading state - show until we have questions loaded OR there's an error
  if (!isPageReady) {
    return <ChestionarLoadingState />;
  }

  // Error state - Fallback for other generic errors (Network, etc)
  if (error) {
    // The specific link expired/used errors are handled above in the HARD GUARD
    // This is just for generic errors
    return (
      <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 border-2 border-red-100 mb-6">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Eroare</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all"
          >
            Reîncarcă pagina
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <SuccessPage 
        hospitalId={feedbackContext?.hospital_id}
        hospitalName={feedbackContext?.hospital_name}
        logoUrl={feedbackContext?.logo_url}
        successTitle={uiCopy.success_title}
        successDescription={uiCopy.success_description}
        successTopText={uiCopy.success_top_text}
        successBottomText={uiCopy.success_bottom_text}
      />
    );
  }

  // Main questionnaire form
  return (
    <>
    <div className="min-h-screen bg-slate-50 font-sans">
      
      {/* --- REDESIGNED HERO SECTION --- */}
      <div className="relative w-full max-w-full overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 pt-8 pb-24 sm:pt-16 sm:pb-40">
        {/* Background decorative elements - reduced opacity */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-pulse" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500 rounded-full mix-blend-overlay filter blur-3xl opacity-15" />
        <div className="absolute top-8 left-8 transform -rotate-12 opacity-5">
          <svg className="w-24 h-24 sm:w-32 sm:h-32 text-white" strokeWidth={3} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-10 text-center break-words">

          {/* Top Label - Hospital Name */}
          <div className="mb-6 sm:mb-10">
            <p className="text-xs sm:text-base font-bold tracking-wider sm:tracking-widest text-blue-100 uppercase opacity-90 break-words">
              {feedbackContext?.hospital_name || 'Feedback Pacienți'}
            </p>
          </div>

          {/* Hospital Logo - Standalone, No Bubble. Hidden when ui_copy.show_logo is explicitly false. */}
          {uiCopy.show_logo !== false && (
            <div className="mb-8 sm:mb-10 flex justify-center px-4">
              {feedbackContext?.logo_url ? (
                <div className="bg-white rounded-2xl p-2 sm:p-6 shadow-lg">
                  <img
                    src={feedbackContext.logo_url}
                    alt={`${feedbackContext.hospital_name || 'Hospital'} logo`}
                    crossOrigin="anonymous"
                    className="h-36 w-auto max-w-[95vw] sm:h-36 sm:max-w-[400px] md:h-60 md:max-w-[380px] lg:h-48 lg:max-w-[450px] object-contain"
                    onError={(e) => {
                      // Fallback if logo fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallbackDiv = target.parentElement?.nextElementSibling as HTMLElement;
                      if (fallbackDiv) {
                        fallbackDiv.classList.remove('hidden');
                        fallbackDiv.classList.add('flex');
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="h-32 sm:h-32 md:h-40 lg:h-48 flex items-center justify-center px-8">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-md">
                    {feedbackContext?.hospital_name || 'Spital'}
                  </h2>
                </div>
              )}
              {/* Fallback text (hidden by default, shown if image fails) */}
              {feedbackContext?.logo_url && (
                <div className="hidden h-32 sm:h-32 md:h-40 lg:h-48 items-center justify-center px-8 bg-white rounded-2xl p-6 sm:p-6 shadow-lg">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-700">
                    {feedbackContext?.hospital_name || 'Spital'}
                  </h2>
                </div>
              )}
            </div>
          )}

          {/* Main Headline */}
          <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3 sm:mb-5 tracking-tight drop-shadow-md break-words hyphens-auto">
            {getUiText(uiCopy.questionnaire_title, 'Părerea ta contează')}
          </h1>

          {/* Section context sentence — names the specific section a patient's
              feedback is being requested for (resolved from the link's
              cod_sectie via nomenclator_sectii.denumire). Crucial when the
              same patient has multiple stays at the same hospital and gets
              one link per stay. Hidden if the section couldn't be resolved. */}
          {feedbackContext?.section_name && (
            <div className="mb-3 sm:mb-4 flex justify-center">
              <p className="max-w-md mx-auto px-4 py-2 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/30 text-white text-sm sm:text-base font-bold leading-relaxed drop-shadow-sm break-words hyphens-auto text-justify">
                Vă rugăm să completați următorul chestionar în urma experienței pe care ați avut-o în <span className="font-bold">{feedbackContext.section_name}</span>.
              </p>
            </div>
          )}

          {/* Subtitle — no default copy. An empty "Descriere" in the hospital's
              config renders nothing at all (element omitted, not blanked, so
              no leftover gap). The title above still falls back, since only
              Descriere was asked to stop having a default. */}
          {uiCopy.questionnaire_description?.trim() && (
            <p className="text-[13px] sm:text-base md:text-lg text-blue-50 leading-relaxed max-w-md mx-auto drop-shadow-sm break-words whitespace-pre-line">
              {uiCopy.questionnaire_description.trim()}
            </p>
          )}
        </div>
      </div>

      {/* --- MAIN CONTENT CARD --- */}
      <div className="relative -mt-16 sm:-mt-28 px-2 sm:px-4 pb-8 sm:pb-12">
        <div className="max-w-lg mx-auto w-full bg-white rounded-3xl sm:rounded-[2rem] shadow-2xl shadow-blue-900/10 border border-slate-100 overflow-hidden">

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="px-3 sm:px-6 md:px-8 pt-6 sm:pt-10 pb-6 sm:pb-10 space-y-4 sm:space-y-6">
            {/* Questionnaire top copy — only rendered when non-empty */}
            <UiCopyBlock text={uiCopy.questionnaire_top_text ?? ''} />

            {questions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-500">Se încarcă întrebările...</p>
              </div>
            ) : (
              questions.map((question, index) => (
                <QuestionRenderer
                  key={question.id}
                  question={question}
                  value={answers[question.id] || ''}
                  onChange={(value) => handleAnswerChange(question.id, value)}
                  doctors={question.type === 'doctor_dropdown' ? filteredDoctors : doctors}
                  specializations={specializations}
                  autoFocus={index === 0}
                  tipServicii={feedbackContext?.tip_servicii}
                  isFirstQuestion={index === 0}
                />
              ))
            )}
            
            {/* Questionnaire bottom copy — only rendered when non-empty */}
            <UiCopyBlock text={uiCopy.questionnaire_bottom_text ?? ''} />

            <button
              type="submit"
              disabled={submitting || questions.length === 0 || submitted}
              className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold py-4 sm:py-4.5 px-6 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg touch-manipulation mt-8"
            >
              {submitted ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Chestionar trimis</span>
                </span>
              ) : submitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:h-6 border-b-2 border-white"></div>
                  <span>Se trimite...</span>
                </div>
              ) : (
                'Trimite Feedback'
              )}
            </button>

            {/* SMS Unsubscribe — placed below the submit button per ops
                feedback: respondents fill the form first; the opt-out
                choice is a secondary, after-the-fact decision and
                shouldn't draw attention away from the primary CTA.
                Sized down for the same reason: at w-5 + text-base + bold
                slate-700 it read as loud as the questions above it. The
                box is 14px and the label is small and un-bolded, but the
                <label> still wraps the whole text block, so the tap target
                stays finger-sized on mobile. */}
            <label className="flex items-start gap-2 cursor-pointer group pt-4">
              <input
                type="checkbox"
                checked={unsubscribe}
                onChange={(e) => setUnsubscribe(e.target.checked)}
                className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer touch-manipulation"
              />
              <div>
                <span className="text-[11px] sm:text-xs font-normal text-slate-500 leading-snug">
                  Nu mai doresc să primesc mesaje SMS legate de feedback.
                </span>
                <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">
                  Te poți răzgândi ulterior, spitalul poate reactiva numărul.
                </p>
              </div>
            </label>
          </form>
          
          <p className="text-center text-slate-400 text-xs pb-8 pt-6 px-6">
            © 2026 Powered by AXA Medis. Toate drepturile rezervate.
          </p>
        </div>
      </div>
    </div>

    {/* Validation Modal */}
    {showValidationModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 sm:p-8">
          <div className="mb-5 sm:mb-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-amber-50 border-2 border-amber-200">
              <svg className="h-8 w-8 sm:h-10 sm:w-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center">
            {validationMessage?.title ?? 'Câmpuri obligatorii incomplete'}
          </h3>

          <div className="mb-5 sm:mb-6">
            {validationMessage ? (
              <p className="text-gray-600 text-center text-sm sm:text-base">
                {validationMessage.body}
              </p>
            ) : (
              <>
                <p className="text-gray-600 text-center mb-3 sm:mb-4 text-sm sm:text-base">
                  Vă rugăm să completați următoarele câmpuri obligatorii:
                </p>
                <ul className="space-y-2">
                  {missingRequiredFields.map((field) => (
                    <li key={field.id} className="flex items-center text-sm sm:text-base text-gray-700">
                      <span className="text-red-500 mr-2">•</span>
                      {field.label}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <button
            onClick={() => { setShowValidationModal(false); setValidationMessage(null) }}
            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white py-4 sm:py-4.5 px-6 rounded-xl font-bold transition-all shadow-lg shadow-blue-200 text-base sm:text-lg touch-manipulation active:scale-[0.98]"
          >
            {validationMessage ? 'Înțeles' : 'Înțeles, completez câmpurile'}
          </button>
        </div>
      </div>
    )}
    </>
  )
}

// Main Component with Error Boundary
const ChestionarPublicPage: React.FC = () => {
  return (
    <ChestionarErrorBoundary>
      <ChestionarPublicPageContent />
    </ChestionarErrorBoundary>
  )
}

export default ChestionarPublicPage